import React, {FC} from 'react';
import styled from 'styled-components';

import {OkCancelDialog} from '../../../../../../../../../src/components/common/dialogs/okCancelDialog';
import {Plural, Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

interface AccountsCsvImportMappingIncompleteDialogProps {
  onClose: Handler;
  onImport: Handler;
  totalInvalidValues: number;
  totalUnmappedHeaders: number;
}

/*
 * Styles.
 */

const StyledBoldSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const AccountsCsvImportMappingIncompleteDialog: FC<AccountsCsvImportMappingIncompleteDialogProps> = (
  props,
) => {
  const {onClose, onImport, totalInvalidValues, totalUnmappedHeaders} = props;
  return (
    <OkCancelDialog
      title={<Trans id="account-csv-mapping-incomplete-dialog-title">Mapping incomplete</Trans>}
      okButtonLabel={<Trans id="account-csv-mapping-incomplete-dialog-ok-button">Import anyway</Trans>}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      onRequestClose={() => onClose()}
      onOkClick={() => {
        onImport();
        onClose();
      }}
    >
      {maybeRenderMessage(totalInvalidValues, totalUnmappedHeaders)}
    </OkCancelDialog>
  );
};

/*
 * Helpers.
 */

function maybeRenderMessage(totalInvalidValues: number, totalUnmappedHeaders: number) {
  if (totalInvalidValues > 0 && totalUnmappedHeaders > 0) {
    return (
      <>
        {renderInvalidValuesCount(totalInvalidValues)}
        <Trans id="accounts-csv-import-dialog-message-part-1">in your CSV and</Trans>{' '}
        {renderUnmappedValuesCount(totalUnmappedHeaders)}
        <Trans id="accounts-csv-import-dialog-message-part-2">
          won&apos;t be imported. Do you want to proceed anyway?
        </Trans>
      </>
    );
  }
  if (totalInvalidValues > 0) {
    return (
      <>
        {renderInvalidValuesCount(totalInvalidValues)}
        {renderProceedAnywayMessage()}
      </>
    );
  }
  if (totalUnmappedHeaders > 0) {
    return (
      <>
        {renderUnmappedValuesCount(totalUnmappedHeaders)}
        {renderProceedAnywayMessage()}
      </>
    );
  }
  return null;
}

function renderInvalidValuesCount(totalInvalidValues: number) {
  return (
    <StyledBoldSpan>
      <Plural
        id="accounts-csv-import-dialog-message-invalid-values"
        value={totalInvalidValues}
        one="# invalid value"
        other="# invalid values"
      />{' '}
    </StyledBoldSpan>
  );
}

function renderUnmappedValuesCount(totalUnmappedHeaders: number) {
  return (
    <StyledBoldSpan>
      <Plural
        id="accounts-csv-import-dialog-message-unmapped-columns"
        value={totalUnmappedHeaders}
        one="# unmapped column"
        other="# unmapped columns"
      />{' '}
    </StyledBoldSpan>
  );
}

function renderProceedAnywayMessage() {
  return (
    <Trans id="accounts-csv-import-dialog-message-proceed-anyway">
      in your CSV won&apos;t be imported. Do you want to proceed anyway?
    </Trans>
  );
}
